import { useInfiniteQuery } from '@tanstack/react-query'
import { QueryKeys } from '../queries/querykeys'
import { BusinessAPI } from '../api/business.api'
import { UserAuth } from '../context/AuthContext'
import { GETNET, MERCADOPAGO, TRANSBANK } from '../components/utils/constant'

interface UseInfiniteAdminTransactionsParams {
  businessId: number
  organizationId: number
  activeTab: typeof MERCADOPAGO | typeof GETNET | typeof TRANSBANK
  year: number
  month: number
  limit: number
  filters: {
    searchQuery?: string
    source?: string
  }
}

export const useInfiniteAdminTransactions = ({
  businessId,
  organizationId,
  activeTab,
  year,
  month,
  limit = 50,
  filters
}: UseInfiniteAdminTransactionsParams) => {
  const { isAuthenticated } = UserAuth()
  const businessApi = new BusinessAPI()

  return useInfiniteQuery({
    enabled: isAuthenticated && !!businessId && !!organizationId && activeTab !== MERCADOPAGO,
    queryKey: [
      QueryKeys.ADMIN_TRANSACTIONS,
      businessId,
      organizationId,
      activeTab,
      year,
      month,
      limit,
      filters
    ],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async ({ pageParam = 1 }) => {
      const beginDate = month > 0
        ? `${year}-${String(month).padStart(2, '0')}-01T00:00:00Z`
        : `${year}-01-01T00:00:00Z`

      const endDate = month > 0
        ? `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}T23:59:59Z`
        : `${year}-12-31T23:59:59Z`

      const params = {
        beginDate,
        endDate,
        limit,
        page: pageParam,
        source: filters.source !== 'bank_sources_only' ? filters.source : undefined,
        searchQuery: filters.searchQuery
      }

      const response = await (activeTab === MERCADOPAGO
        ? businessApi.getDigitalPayments(businessId, organizationId, params)
        : businessApi.getBankMovements(businessId, organizationId, params)
      )

      const transactions = response.data.transactions || []
      const total = response.data.total || transactions.length
      const totalPages = Math.ceil(total / limit)

      return {
        data: transactions,
        totalPages,
        currentPage: pageParam,
        hasNextPage: pageParam < totalPages,
        total
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined
    },
    initialPageParam: 1
  })
}
